import { App, View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMProgressBar,
  PMSmallNumber,
  PMSmallNumberCardEditSchema,
} from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { APP_RECORD_TYPES } from "vmd-types";

import { TestAppState } from "../../../app";
import {
  countReportingItemsByRecordType,
  isRecordTypeVisible,
} from "../../../utils/filter-reporting-items-by-record-type";

/**
 * Adds the number-of-tracking-items, number-of-business-apps, reviews-status
 * and (when the record type is visible) number-of-IT-services cards, in that
 * order (#2222).
 */
export function addOverviewNumberCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const language = app.getAppState().language || "en";

  /**
   * Add the number of tracking items component to the view.
   */
  const numberOfTrackingItemsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "numberOfTrackingItemsComponent",
      labels: ["#ofItems"],
      description:
        getLocalizedMessage(language, "showsNumberOfTrackingItems") ||
        "Shows the number of Tracking Items.",
      title:
        getLocalizedMessage(language, "numberOfTrackingItems") ||
        "Tracking Items",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  numberOfTrackingItemsComponent.mapState(({ currentReport, settings }) => {
    return {
      title: `${
        settings?.configuration?.itemName ??
        getLocalizedMessage(language, "trackingItems") ??
        settings?.configuration?.itemName ??
        "Tracking Items"
      }`,
      number: currentReport?.project?._calculated?.numberOfSubEntities ?? 0,
    };
  });

  const numberOfBusinessApplicationsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "numberOfBusinessApplicationsComponent",
      labels: ["#ofBusinessApplications"],
      description: "Shows the number of DC Business Apps.",
      title: "DC Business Apps",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  numberOfBusinessApplicationsComponent.mapState(({ currentReport }) => ({
    title: "DC Business Apps",
    number: countReportingItemsByRecordType(
      currentReport?.project?.reportingItems,
      APP_RECORD_TYPES.BUSINESS_APPLICATION
    ),
  }));

  const reviewsStatusComponent = view.addComponent({
    component: PMProgressBar,
    layoutProps: {
      componentId: "reviewsStatusDashboardComponent",
      labels: ["reviewsStatus", "progress"],
      description: "Shows submitted reviews for this OE's DC Business Apps.",
      title: "Reviews Status",
    },
  });

  reviewsStatusComponent.mapState(({ currentReport }) => ({
    icon: "TrendingUp",
    title: "Reviews Status",
    label: "Reviews Submitted",
    value:
      currentReport?.project?._calculated?.reviewedBusinessApplications ?? 0,
    total: currentReport?.project?._calculated?.totalBusinessApplications ?? 0,
    percent:
      currentReport?.project?._calculated
        ?.reviewedBusinessApplicationsPercent ?? 0,
  }));

  if (isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)) {
    const numberOfITServicesComponent = view.addComponent({
      component: PMSmallNumber,
      layoutProps: {
        componentId: "numberOfITServicesComponent",
        labels: ["#ofITServices"],
        description: "Shows the number of IT Services.",
        title: "IT Services",
        editSchema: PMSmallNumberCardEditSchema,
      },
    });

    numberOfITServicesComponent.mapState(({ currentReport }) => ({
      title: "IT Services",
      number: countReportingItemsByRecordType(
        currentReport?.project?.reportingItems,
        APP_RECORD_TYPES.IT_SERVICE
      ),
    }));
  }
}
